<template>
    <div class="page-store">
        <header class="store-title" :style="{ backgroundImage: `url(${collectionImage})`}">
            <h1>Store</h1>
        </header>

        <section class="featured-collection">
            <div class="container">
                <h3 v-if="featuredTitle">{{ featuredTitle }}</h3>
            </div>
            <div class="container">
                <div class="product-list" v-bind:class="{ 'loading-products': loadingCollection }">
                    <article class="product" v-for="(product, index) in collectionProducts" v-bind:key="index">
                        <router-link :to="{ name: 'product', params: { id: product.handle }}">
                            <span class="badge" v-if="product.variants[0].compareAtPrice">On Sale!</span>
                            <span class="badge" v-if="!product.availableForSale">Sold Out</span>
                            <div v-if="product.images" class="thumbnail" :style="{ backgroundImage: `url(${product.images[0].src})`}"></div>
                            <footer class="details">
                                <div class="name">{{ product.title }}</div>
                                <div class="price" v-if="product.variants">
                                    <span class="current-price">{{ product.variants[0].price | currency }}</span>
                                    <span class="compare-price" v-if="product.variants[0].compareAtPrice && product.variants[0].compareAtPrice > 0">
                                        {{ product.variants[0].compareAtPrice | currency }}
                                    </span>
                                </div>
                            </footer>
                        </router-link>
                    </article>
                    <div v-if="!loadingCollection && collectionProducts.length === 0">No products</div>
                </div>
            </div>
        </section>

        <section class="other-products">
            <div class="container">
                <div class="sort-options">
                    <div class="sort-control">
                        <label for="categories">Categories</label>
                        <select id="categories" name="categories" v-model="selectedCategory" @change="onCategoryChange()">
                            <option value="all">
                                All Clothing
                                <span v-if="selectedCategory === 'all'">({{ products ? products.length : 0 }})</span>
                            </option>
                            <option v-for="(category, index) in categories" v-bind:value="category.handle" v-bind:key="index">
                                {{ category.title }}
                                <span v-if="category.handle === selectedCategory">({{ products.length }})</span>
                            </option>
                        </select>
                    </div>

                    <div class="sort-control">
                        <label for="sort">Sort By:</label>
                        <select id="sort" name="sort" v-model="sortBy" @change="onSortChange()">
                            <option value="title-ascending">A-Z</option>
                            <option value="title-descending">Z-A</option>
                            <option value="price-ascending">Price, low to high</option>
                            <option value="price-descending">Price, high to low</option>
                            <option value="created-descending">Date, new to old</option>
                            <option value="created-ascending">Date, old to new</option>
                        </select>
                    </div>
                </div>

                <div class="product-list" v-bind:class="{ 'loading-products': loading }">
                    <article class="product" v-for="(product, index) in products" v-bind:key="index" v-bind:class="{ 'sold-out' : !product.availableForSale }">
                        <router-link :to="{ name: 'product', params: { id: product.handle }}">
                            <span class="badge" v-if="product.variants[0].compareAtPrice">On Sale!</span>
                            <span class="badge" v-if="!product.availableForSale">Sold Out</span>
                            <div v-if="product.images" class="thumbnail" :style="{ backgroundImage: `url(${product.images[0].src})`}"></div>
                            <footer class="details">
                                <div class="name">{{ product.title }}</div>
                                <div class="price" v-if="product.variants">
                                    <span class="current-price">{{ product.variants[0].price | currency }}</span>
                                    <span class="compare-price" v-if="product.variants[0].compareAtPrice && product.variants[0].compareAtPrice > 0">
                                        {{ product.variants[0].compareAtPrice | currency }}
                                    </span>
                                </div>
                            </footer>
                        </router-link>
                    </article>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import ShopifyClient from '@/shopify';
import slugify from 'slugify';

const SORT_OPTIONS = [
    'title-ascending',
    'title-descending',
    'price-ascending',
    'price-descending',
    'created-ascending',
    'created-descending',
];

export default {
    name: 'shop',
    data() {
        return {
            categories: [],
            selectedCategory: 'all',
            products: [],
            sortBy: 'created-descending',
            sortOption: null,
            loading: true,
            loadingCollection: true,
            collectionProducts: [],
            featuredCollection: null,
            featuredTitle: 'Latest Collection',
            collectionImage: '',
        };
    },
    // props: {
    //     'featuredTitle': String,
    // },
    created() {
        const { type, sort } = this.$route.query;

        if (type) {
            this.selectedCategory = type;
        }

        if (sort) {
            this.setSortOption(sort);
        }

        this.setCategories();
        this.loadCollection();
    },
    computed: {
        sortOptions() {
            const options = {
                'title-ascending': { sort: 'TITLE' },
                'title-descending': { sort: 'TITLE', reverse: true },
                'price-ascending': { sort: 'PRICE' },
                'price-descending': { sort: 'PRICE', reverse: true },
                'created-ascending': { sort: 'CREATED_AT' },
                'created-descending': { sort: 'CREATED_AT', reverse: true },
            };
            return options[this.sortBy] || { sort: 'ID' };
        },
    },
    methods: {
        setCategories() {
            const productTypeQuery = ShopifyClient.graphQLClient.query((root) => {
                root.add('shop', (shop) => {
                    shop.addConnection('productTypes', { args: { first: 30 } });
                });
            });

            ShopifyClient.graphQLClient.send(productTypeQuery).then(({ data }) => {
                this.categories = data.shop.productTypes.edges.map((type) => {
                    return {
                        title: type.node,
                        handle: slugify(type.node, { lower: true }),
                    };
                });

                this.loadProducts();
            });
        },
        onSortChange() {
            this.$router.push({
                name: 'shop',
                query: {
                    ...this.$route.query,
                    sort: this.sortBy,
                },
            });
        },
        onCategoryChange() {
            this.$router.push({
                name: 'shop',
                query: {
                    ...this.$route.query,
                    type: this.selectedCategory,
                },
            });
        },
        loadCollection() {
            this.loadingCollection = true;

            // window.__COLLECTION__ = {
            //     id: 'Z2lkOi8vc2hvcGlmeS9Db2xsZWN0aW9uLzkxNTAzMzYyMTQ4',
            //     image: 'https://i.ibb.co/kgHSsvy/store-header.jpg',
            // };

            // ShopifyClient.collection.fetchAllWithProducts().then((collections) => {
            //     // Do something with the collections
            //     console.log(collections);
            // });

            if (!window.__COLLECTION__) {
                this.loadingCollection = false;
                return;
            }

            this.collectionImage = window.__COLLECTION__.image;

            ShopifyClient.collection.fetchWithProducts(__COLLECTION__.id).then((collection) => {
                this.featuredCollection = collection;
                this.loadingCollection = false;
                this.collectionProducts = collection.products;
            });
        },
        loadProducts() {
            this.loading = true;

            const options = {
                first: 100,
                ...this.sortOptions,
            };

            if (this.selectedCategory !== 'all') {
                const category = this.categories.find((category) => category.handle === this.selectedCategory);
                options.query = `product_type:${category.title}`;
            }

            ShopifyClient.product.fetchQuery(options).then((response) => {
                this.products = response.filter(item => item.type.name === 'Product');
                this.loading = false;
            });
        },
        setSortOption(sortBy) {
            if (SORT_OPTIONS.includes(sortBy)) {
                this.sortBy = sortBy;
            } else {
                this.$router.replace({
                    name: 'shop',
                    query: { ...this.$route.query, sort: 'title-ascending' },
                });
            }
        },
    },
    watch: {
        '$route' (to) {
            if (to.query.type) {
                this.selectedCategory = to.query.type;
            }

            if (to.query.sort && !SORT_OPTIONS.includes(to.query.sort)) {
                this.setSortOption(to.query.sort);
                return;
            }

            this.loadProducts();
        },
    },
};
</script>

<style lang="scss" scoped>
.page-store {
    min-height: 500px;
}
.loading-products {
    filter: blur(1px);
    user-select: none;
    pointer-events: none;
    opacity: 0.5;
}
</style>
